import axios from 'axios';
import { adminApi } from '@/includes/contentManagementApi';

export default class NetworkAssessment {
  /**
   * Get full assessment for questionnaire/intro (schema config for intro is included).
   * Migrated to Monorepo: POST /content-management-api/assessments/getAssessment
   */
  static getAssessment(data, config) {
    return adminApi.post('assessments/getAssessment', { id: data }, config);
  }

  /**
   * Authenticate an external user via their assessment-specific token.
   * This exchanges the external access token for a temporary JWT that
   * can be used for assessment operations (updateAnswer, submitAssessment).
   *
   * Uses a raw axios instance (no interceptors) because:
   * - The endpoint is @Public() on the backend (no JWT required)
   * - We don't want the adminApi 401 interceptor to redirect before the caller can handle errors
   *
   * @param {string} assessmentId - The assessment ID
   * @param {string} externalToken - The external access token from the email link
   * @returns {Promise} Promise resolving to { token, assessmentId, userId }
   */
  static authenticateExternal(assessmentId, externalToken) {
    // Use a clean axios instance without auth interceptors for this public endpoint
    const publicApi = axios.create({
      baseURL: '/content-management-api',
      headers: { 'Content-Type': 'application/json' },
    });
    return publicApi.post('/assessments/external/auth', {
      assessmentId,
      externalToken,
    });
  }

  /**
   * Update a single answer in an assessment (incremental update)
   * This method sends only the changed answer, reducing bandwidth by 90%+
   *
   * @param {string} assessmentId - The assessment ID
   * @param {number} questionIndex - The index of the question (0-based)
   * @param {object} answer - The answer data object
   * @param {number} progress - Optional progress (0-100).
   *  If not provided, will be calculated server-side
   * @param {object} config - Optional axios config
   * @returns {Promise} Promise resolving to the updated assessment
   */
  static updateAnswer(assessmentId, questionIndex, answer, progress, config) {
    const payload = {
      answer,
    };

    // Include progress if provided
    if (progress !== undefined && progress !== null) {
      payload.progress = progress;
    }

    return adminApi.patch(
      `/assessments/${assessmentId}/answers/${questionIndex}`,
      payload,
      config,
    );
  }

  /**
   * Update entire assessment (legacy method - DEPRECATED)
   * @deprecated This method is deprecated. Use updateAnswer() instead for better performance.
   * The old backend endpoint will be removed after migration is complete.
   * This method is kept temporarily for reference only and should not be used in new code.
   */
  static updateAssessment(data, config) {
    console.warn('updateAssessment() is deprecated. Use updateAnswer() instead.');
    return axios.post('assessment/updateAssessment', data, config);
  }

  /**
   * Submit an assessment
   * Uses the new backend endpoint for better performance and cleaner API
   * @param {string} assessmentId - The assessment ID to submit
   * @param {object} config - Optional axios config
   * @returns {Promise} Promise resolving to { success: boolean }
   */
  static submitAssessment(assessmentId, config) {
    return adminApi.post(`/assessments/${assessmentId}/submit`, {}, config);
  }

  /**
   * Submit assessment (legacy method - DEPRECATED)
   * @deprecated This method is deprecated. The new submitAssessment() method
   * only requires the assessment ID instead of the full assessment object.
   * This method is kept temporarily for backward compatibility but should not be used.
   */
  static submitAssessmentLegacy(data, config) {
    console.warn('submitAssessmentLegacy() is deprecated. Use submitAssessment(assessmentId) instead.');
    return axios.post('assessment/submitAssessment', data, config);
  }

  static cancelAssessment(data, config) {
    const assessmentId = data?.assessmentID || data?.assessmentId || data?._id;
    const reasons = Array.isArray(data?.reasons) ? data.reasons : [];
    return adminApi.post(`/assessments/kss/${assessmentId}/cancel`, { reasons }, config);
  }

  static getStickyQuestionResults(schemaId) {
    return adminApi.get(`/assessments/user/sticky-results/${schemaId}`);
  }
}
